import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Star, Loader2, BadgeCheck } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { attorneyText } from "@/lib/attorneyI18n";

const StatLine = ({ s, testid, L }) => (
  <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground mt-2" data-testid={testid}>
    <span><strong className="text-foreground text-sm">{s.this_month.views}</strong> {L.views}</span>
    <span><strong className="text-foreground text-sm">{s.this_month.calls}</strong> {L.calls}</span>
    <span><strong className="text-foreground text-sm">{s.this_month.web_clicks}</strong> {L.web}</span>
    <span className="opacity-80">{L.thisMonth(s.last_month.views, s.last_month.calls)}</span>
  </div>
);

const CountyRow = ({ listing, county, spot, taken, slots, price, onBuy, onCancel, busy, stats, L, highlight }) => {
  const key = `${listing.id}-${county}`;
  const full = !spot && taken >= slots;
  return (
    <div className={`p-4 flex flex-wrap items-center justify-between gap-3 ${highlight ? "ring-2 ring-accent ring-inset bg-accent/5" : ""}`} data-testid={`featured-row-${key}`}
      ref={highlight ? (el) => el?.scrollIntoView({ block: "center" }) : undefined} data-highlight={highlight ? "true" : undefined}>
      <div>
        <p className="font-medium text-foreground">{L.countyName(county)}</p>
        <p className="text-xs text-muted-foreground">{spot ? L.featuredHere : L.open(Math.max(slots - taken, 0), slots)}</p>
        {spot && stats && <StatLine s={stats} testid={`featured-stats-${key}`} L={L} />}
      </div>
      {spot ? (
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-testid={`featured-active-${key}`}><Star size={13} /> {L.featured}</span>
          <Button size="sm" variant="ghost" onClick={() => onCancel(spot)} disabled={busy === spot.id} data-testid={`featured-cancel-${key}`}>
            {busy === spot.id ? <Loader2 size={14} className="animate-spin" /> : L.cancel}
          </Button>
        </div>
      ) : (
        <Button size="sm" onClick={() => onBuy(listing, county)} disabled={full || busy === key} data-testid={`featured-buy-${key}`} className="rounded-sm gap-2">
          {busy === key ? <Loader2 size={14} className="animate-spin" /> : <Star size={14} />} {full ? L.full : L.featureMe(price)}
        </Button>
      )}
    </div>
  );
};

export default function FeaturedSpots() {
  const { lang } = useI18n();
  const L = attorneyText(lang);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();
  const focusCounty = params.get("county");

  const [stats, setStats] = useState({});
  const load = useCallback(() => Promise.all([
    api.get("/featured/mine").then(({ data }) => setData(data)),
    api.get("/featured/stats").then(({ data }) => setStats(data.stats)).catch(() => {}),
  ]), []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { load(); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data: s } = await api.get(`/payments/status/${sid}`);
        if (s.payment_status === "paid") {
          if (s.featured_result) toast.error(L.paidFull);
          else toast.success(L.paidOk);
          setParams({}); return load();
        }
      } catch { /* keep polling */ }
      if (++tries < 8) setTimeout(poll, 2000); else { setParams({}); load(); }
    };
    poll();
  }, [params, setParams, load]);

  const buy = async (listing, county) => {
    setBusy(`${listing.id}-${county}`);
    try {
      const { data: r } = await api.post("/featured/checkout", { attorney_id: listing.id, county, origin_url: window.location.origin });
      window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || L.checkoutErr);
      setBusy("");
    }
  };

  const cancel = async (spot) => {
    if (!window.confirm(L.confirmCancel(spot.county))) return;
    setBusy(spot.id);
    try { await api.post(`/featured/${spot.id}/cancel`); toast.success(L.canceled); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || L.cancelErr); }
    finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 w-full max-w-4xl mx-auto px-6 py-16" data-testid="featured-page">
        <p className="overline text-accent mb-3 flex items-center gap-2"><Star size={14} /> {L.fOver}</p>
        <h1 className="font-serif font-bold text-4xl sm:text-5xl tracking-tighter mb-4">{L.fH1}</h1>
        <p className="text-muted-foreground max-w-2xl mb-10">
          {L.fIntro}
          {data && L.fTerms(data.price, data.slots)}
        </p>
        {!data && <p className="text-muted-foreground animate-pulse">{L.loading}</p>}
        {data && data.listings.length === 0 && (
          <div className="grid-card p-8" data-testid="featured-no-listing">
            <p className="font-serif text-xl text-foreground mb-2">{L.claimFirst}</p>
            <p className="text-muted-foreground mb-5">{L.claimFirstBody}</p>
            <Link to="/find-attorney"><Button className="rounded-sm" data-testid="featured-claim-link">{L.findClaim}</Button></Link>
          </div>
        )}
        {data && data.listings.map((l) => (
          <div key={l.id} className="mb-8" data-testid={`featured-listing-${l.id}`}>
            <p className="font-serif text-2xl text-foreground flex items-center gap-2">{l.name} <BadgeCheck size={18} className="text-emerald-500" /></p>
            {l.firm && <p className="text-sm text-muted-foreground mb-3">{l.firm}</p>}
            <div className="grid-card divide-y divide-border">
              {(l.counties || []).map((c) => (
                <CountyRow key={c} listing={l} county={c} slots={data.slots} price={data.price} busy={busy}
                  spot={data.spots.find((s) => s.attorney_id === l.id && s.county === c)} taken={data.slots_taken[c] || 0}
                  L={L} highlight={c === focusCounty} onBuy={buy} onCancel={cancel} stats={stats[data.spots.find((s) => s.attorney_id === l.id && s.county === c)?.id]} />
              ))}
            </div>
          </div>
        ))}
      </main>
      <Footer />
    </div>
  );
}
